/**
 * src/components/shared/AssignmentBanner.jsx — TICKET-030
 *
 * Top-of-view banner that surfaces unread task assignments. Workers see
 * "N new assignments" with inline Accept/Open/Dismiss actions. CEO + PMO
 * see assignments routed to them (rare; usually they assign downward).
 *
 * Realtime-fed by useNotifications. Renders nothing when unreadCount === 0.
 *
 * Mounted in FocusView + MyDayView per CEO directive 2026-04-27 ("both").
 */

import { useState, useMemo } from 'react';
import { Bell, Check, ChevronDown, ChevronUp, X, ArrowRight } from 'lucide-react';
import useNotifications from '../../lib/hooks/useNotifications';
import { acceptTask, fetchTaskWorkTag } from '../../lib/api/tasks';
import { requestQuoteOpen, quoteIdFromTask } from '../../lib/nav/quoteNav';
import { useT } from '../../lib/i18n/I18nContext';

function relativeTime(iso, locale = 'en') {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return '';
  const min = Math.floor(ms / 60000);
  if (min < 1) return locale === 'es' ? 'ahora' : 'now';
  if (min < 60) return locale === 'es' ? `hace ${min} min` : `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return locale === 'es' ? `hace ${hr} h` : `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return locale === 'es' ? `hace ${days} d` : `${days}d ago`;
}

const PILL_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 11,
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 99,
  background: 'var(--accent)',
  color: 'var(--inv)',
};

const ICON_BTN_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 44,
  minHeight: 44,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: 8,
  color: 'var(--t2)',
};

const ACCEPT_BTN_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  minHeight: 44,
  padding: '8px 14px',
  border: 'none',
  borderRadius: 10,
  background: 'var(--ok)',
  color: 'var(--inv)',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const OPEN_BTN_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  minHeight: 44,
  padding: '8px 12px',
  border: '1px solid var(--bdr)',
  borderRadius: 10,
  background: 'var(--glass)',
  color: 'var(--t1)',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

export default function AssignmentBanner({ onOpenTask } = {}) {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications({ unreadOnly: true });
  const { t, locale } = useT();
  const [expanded, setExpanded] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const visible = useMemo(
    () => notifications.filter((n) => !n.read_at).slice(0, 8),
    [notifications],
  );

  if (unreadCount === 0) return null;

  const summaryLabel =
    unreadCount === 1
      ? t('notifications.newAssignmentOne', { defaultValue: '1 new assignment' })
      : t('notifications.newAssignmentsMany', { count: unreadCount, defaultValue: `${unreadCount} new assignments` });

  const handleAccept = async (notification) => {
    setBusyId(notification.id);
    try {
      // Mark notification read first (optimistic UI shrink), then accept the
      // task. If the task acceptance fails, the notification is still read —
      // user can re-open the task to act on it.
      await markRead(notification.id);
      if (notification.task_id) await acceptTask(notification.task_id);
    } finally {
      setBusyId(null);
    }
  };

  // TICKET-1098: navigate FIRST, mark read only once something opened — the old
  // order erased the row before (or instead of) opening anything, so the CEO lost
  // which quote it was. A quote-review task opens its quote directly.
  const handleOpen = async (notification) => {
    setBusyId(notification.id);
    try {
      const taskId = notification.task_id;
      let quoteId = null;
      try {
        quoteId = taskId ? quoteIdFromTask({ work_tag: await fetchTaskWorkTag(taskId) }) : null;
      } catch {
        // spine-emit-ok: lookup failed (offline) — fall back to the task and keep the row unread
        if (typeof onOpenTask === 'function' && taskId) onOpenTask(taskId);
        return;
      }
      if (quoteId) requestQuoteOpen(quoteId);
      else if (typeof onOpenTask === 'function' && taskId) onOpenTask(taskId);
      else if (taskId) window.dispatchEvent(new CustomEvent('acn:search-open', { detail: { type: 'task', id: taskId } }));
      await markRead(notification.id);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      data-testid="assignment-banner"
      role="region"
      aria-label={summaryLabel}
      style={{
        margin: '12px 16px 0',
        padding: '10px 14px',
        borderRadius: 12,
        background: 'var(--info-tint)',
        border: '1px solid var(--bdr)',
        animation: 'fadeIn 0.2s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Bell size={18} strokeWidth={1.5} style={{ color: 'var(--accent)' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--t1)' }}>
            {summaryLabel}
          </div>
          {!expanded && (
            <div style={{ fontSize: 12, color: 'var(--t2)', marginTop: 2 }}>
              {t('notifications.tapToReview', { defaultValue: 'Tap to review' })}
            </div>
          )}
        </div>
        <span style={PILL_STYLE} aria-hidden="true">{unreadCount}</span>
        <button
          type="button"
          data-testid="assignment-banner-toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={expanded ? t('notifications.collapse', { defaultValue: 'Collapse' }) : t('notifications.expand', { defaultValue: 'Expand' })}
          style={ICON_BTN_STYLE}
        >
          {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
        <button
          type="button"
          data-testid="assignment-banner-dismiss-all"
          onClick={() => markAllRead()}
          aria-label={t('notifications.dismissAll', { defaultValue: 'Dismiss all' })}
          style={ICON_BTN_STYLE}
        >
          <X size={18} />
        </button>
      </div>

      {expanded && (
        <ul
          data-testid="assignment-banner-list"
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '10px 0 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {visible.map((n) => {
            const taskName = n.payload?.task_name || t('notifications.untitledTask', { defaultValue: 'Untitled task' });
            const assigner = n.payload?.assigner_name || t('notifications.someone', { defaultValue: 'Someone' });
            const kindLabel =
              n.kind === 'task.reassigned_in'
                ? t('notifications.reassignedIn', { defaultValue: 'Reassigned to you' })
                : n.kind === 'task.reassigned_out'
                ? t('notifications.reassignedOut', { defaultValue: 'Reassigned away' })
                : t('notifications.assignedFrom', { from: assigner, defaultValue: `from ${assigner}` });
            const time = relativeTime(n.created_at, locale);
            const isReassignedOut = n.kind === 'task.reassigned_out';
            const busy = busyId === n.id;

            return (
              <li
                key={n.id}
                data-testid="assignment-banner-row"
                style={{
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: 'var(--glass)',
                  border: '1px solid var(--bdr)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: 'var(--t1)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {taskName}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--t2)', marginTop: 2 }}>
                    {kindLabel}
                    {time ? ` · ${time}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {!isReassignedOut && (
                    <button
                      type="button"
                      data-testid="assignment-banner-accept"
                      disabled={busy}
                      onClick={() => handleAccept(n)}
                      style={{ ...ACCEPT_BTN_STYLE, opacity: busy ? 0.5 : 1 }}
                    >
                      <Check size={16} />
                      {t('task.accept', { defaultValue: 'Accept' })}
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="assignment-banner-open"
                    disabled={busy}
                    onClick={() => handleOpen(n)}
                    style={{ ...OPEN_BTN_STYLE, opacity: busy ? 0.5 : 1 }}
                  >
                    <ArrowRight size={16} />
                    {t('notifications.open', { defaultValue: 'Open' })}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
